export const VendorStatus = {
  Pending: 'pending',
  Active: 'active',
  Suspended: 'suspended',
} as const

export type VendorStatus = (typeof VendorStatus)[keyof typeof VendorStatus]

export const CATALOG_KIND_STANDARD = 'standard'

export const DEFAULT_VENDOR_PREP_MINUTES = 20

export const LOCATION_COORD_DECIMALS = 4

export const STATUS_TOGGLE = 'Toggle'

export const VENDOR_BADGES = {
  express: 'Express',
  standard: 'Standard',
  active: 'Active',
  inactive: 'Inactive',
} as const

export const EMPTY_DISPLAY = '—'

export const VENDOR_DATA_ERRORS = {
  unlinked: 'Vendor account is not linked to a business.',
  loadFailed: 'Could not load vendor data.',
} as const

export type VendorLocation = {
  id: string
  vendorId: string
  name: string
  address: string | null
  latitude: number
  longitude: number
  supportsExpress: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type Vendor = {
  id: string
  name: string
  phone: string
  status: VendorStatus
  location: VendorLocation | null
  createdAt: string
  updatedAt: string
}

export type VendorProduct = {
  id: string
  vendorLocationId: string
  productId: string
  prepTimeMinutes: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type CatalogProduct = {
  id: string
  name: string
  slug: string
  kind: string
  status: string
}

export type LocationFormState = {
  name: string
  address: string
  latitude: string
  longitude: string
  supportsExpress: boolean
  isActive: boolean
}

export type ProductFormState = {
  productIds: string[]
  prepTimeMinutes: string
  isActive: boolean
}

export const EMPTY_LOCATION_FORM: LocationFormState = {
  name: '',
  address: '',
  latitude: '',
  longitude: '',
  supportsExpress: true,
  isActive: true,
}

export const EMPTY_PRODUCT_FORM: ProductFormState = {
  productIds: [],
  prepTimeMinutes: String(DEFAULT_VENDOR_PREP_MINUTES),
  isActive: true,
}

export type Metric = {
  id: string
  label: string
  value: string
  hint: string
  hintTone: 'up' | 'down-good' | 'warn' | 'danger' | 'neutral'
}

export type MetricCardsProps = {
  metrics: Metric[]
}

export const INVENTORY_PAGE = {
  title: 'Products',
  subtitle: 'Add catalog products to your shop',
  listTitle: 'Assigned products',
  add: 'Add products',
  modalTitle: 'Add catalog products',
  emptyLocations: 'Add your shop location before adding products.',
  empty: 'No products assigned yet.',
  allAssigned: 'Every catalog product is already assigned.',
  required: 'Choose at least one product.',
  saveError: 'Could not add these products.',
  confirmRemove: 'Remove this product?',
  save: 'Add products',
  prepLabel: 'Prep time (minutes)',
  activeLabel: 'Active',
  colProduct: 'Product',
  colPrep: 'Prep (min)',
  colStatus: 'Status',
  cancel: 'Cancel',
  remove: 'Remove',
} as const

export const OVERVIEW_PRODUCTS_LIMIT = 6

export const OVERVIEW_PAGE = {
  title: 'Vendor overview',
  subtitle: 'Your shop and products at a glance',
  locationsTitle: 'Location',
  productsTitle: 'Products',
  productsLink: 'All products',
  emptyLocations: 'No shop location yet. Add one to start offering products.',
  emptyProducts: 'No products assigned yet.',
  metricSales: 'Sales today',
  metricOpenOrders: 'Open orders',
  metricDelivered: 'Delivered today',
  metricToday: 'Orders today',
  hintOrders: (count: number) => (count === 1 ? '1 order' : `${count} orders`),
  hintToConfirm: (count: number) =>
    count === 1 ? '1 to confirm' : `${count} to confirm`,
  hintInProgress: 'in progress',
  hintCompleted: 'completed today',
  hintPlaced: 'placed today',
  manage: 'Manage',
  addLocation: 'Add location',
  notFound: 'Vendor not found.',
} as const

export const LOCATIONS_PAGE = {
  title: 'Location',
  subtitle: 'The shop that fulfills your orders',
  listTitle: 'Your shop',
  add: 'Add location',
  edit: 'Edit',
  editTitle: 'Edit location',
  createTitle: 'Add location',
  empty: 'No shop location yet. Add one to start adding products.',
  required: 'Name, latitude, and longitude are required. Search for a location first.',
  saveError: 'Could not save location.',
  nameLabel: 'Name',
  addressLabel: 'Address',
  latitudeLabel: 'Latitude',
  longitudeLabel: 'Longitude',
  expressLabel: 'Supports express',
  activeLabel: 'Active',
  save: 'Save changes',
  cancel: 'Cancel',
  colName: 'Name',
  colAddress: 'Address',
  colCoords: 'Coords',
  colExpress: 'Express',
  colStatus: 'Status',
} as const
